import { styled } from "@mui/material/styles";
import { AutocompleteChangeReason } from "@mui/material/useAutocomplete";
import JsonResponse from "csi/comm/JsonResponse";
import enums from "csi/enums";
import { ControllerSaveFunction } from "csi/middleware/common";
import { submitWriter } from "csi/service/writerService";
import { C8Node, ICache } from "csi/typings";
import { ViewWidget } from "csi/view/Widget";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { SplitButtonItem } from "designSystem/components/button/SplitButton";
import { RadioOption } from "designSystem/components/radio/RadioGroup";
import WizardStore from "designSystem/components/wizard/WizardStore";
import { Text } from "designSystem/index";
import { ILocaleProvider } from "designSystem/locale";
import { red } from "designSystem/styles/color";
import { ComponentItem } from "designSystem/types/component-item-types";
import _ from "lodash";
import { action, makeObservable, observable } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";

export interface AdditionalParameter {
	/** Key of Key-Value pair of Authentication/Connector Parameter */
	key: string;
	/** Type of Authentication/Connector Parameter (Body, Header, Query) */
	type: string;
	/** Value of the Key-Value pair of Authentication/Connector Parameter */
	value: string;
}

interface APIField {
	/** Label value of form field */
	label: string;
	/** value inputted into form field */
	value: any;
	/** attribute flags associated with field */
	flags: any;
}

interface RequiredFields {}

interface RequiredAPIKeyFields extends RequiredFields {
	apiKey: string;
	apiKeyName: string;
	apiKeyAddTo: string;
}

interface RequiredOauthFields extends RequiredFields {
	clientID: string;
	clientSecret: string;
	tokenURL: string;
}

interface RequiredConnectorFields extends RequiredFields {
	tokenValidationURL: string;
	connectionTimeoutMS: number;
	readTimeoutMS: number;
}


export enum APISetupType {
	AUTHENTICATION = "Authentication",
	CONNECTOR = "Connector",
	GENERAL = "General",
}

export const EXTERNAL_API_FORM_REQUIRED_LABEL = {
	alignItems: "flex-start",
	"& .MuiFormControlLabel-asterisk": {
		display: "inline-block",
		color: red[500],
		padding: "0 0 12px 2px",
	},
};

export const TitleText = styled(Text)(() => ({
	fontWeight: 800,
	marginBottom: 8,
	textAlign: "start",
}));

export const WIDTH_SPACE = {
	width: "100%",
	justifyContent: "space-between",
	paddingBottom: "10px",
};

export const applyTextFieldStyle = (widthVal: string): Object => {
	return {
		width: widthVal,
		minWidth: "150px",
	};
};

/** Module and operation names */
const MODULE_SITEADMIN: string = "SiteAdmin";
const OPERATION_API_KEY_CONNECTOR_SETUP: string = "ExternalAPIConnectorSetupAPIKeyCredentials";
const OPERATION_OAUTH_CONNECTOR_SETUP: string = "ExternalAPIConnectorSetupOAuthCredentials";

/** Preset Grant Type value for all setup forms */
const CLIENT_CREDENTIALS: string = "client_credentials";

/** Valid node types for APIAuthCredentials node */
const API_KEY_AUTH_TYPE = "APIKeyCredentials";
const OAUTH2_AUTH_TYPE = "OAuth2Credentials";

/** Maps authentication types to their corresponding operation */
const AUTH_TYPE_OPERATION_MAP: Map<string, string> = new Map([
	[API_KEY_AUTH_TYPE, OPERATION_API_KEY_CONNECTOR_SETUP],
	[OAUTH2_AUTH_TYPE, OPERATION_OAUTH_CONNECTOR_SETUP],
]);

/** API Key Form fields name */
export const API_KEY_NAME: string = "KeyName";
export const API_KEY: string = "Key";
export const API_KEY_ADD_TO: string = "APIKeyAddTo";

/** OAuth2.0 Form fields name */
export const CLIENT_ID: string = "ClientId";
export const CLIENT_SECRET: string = "ClientSecret";
export const GRANT_TYPE: string = "GrantType";
export const SCOPE: string = "Scope";
export const TOKEN_URL: string = "AuthenticationServerURL";

/** ExternalAPIConnector Form fields name */
const PARENT_URL: string = "ParentURL";
const AUTH_BODY_PARAMETERS: string = "AuthBodyParameters";
const AUTH_HEADER_PARAMETERS: string = "AuthHeaderParameters";
const AUTH_QUERY_PARAMETERS: string = "AuthQueryParameters";
const CONNECTOR_BODY_PARAMETERS: string = "ConnectorBodyParameters";
const CONNECTOR_HEADER_PARAMETERS: string = "ConnectorHeaderParameters";
const CONNECTOR_QUERY_PARAMETERS: string = "ConnectorQueryParameters";
const CONNECTOR_GENERAL_PROPERTIES: string = "ConnectorGeneralProperties";

export const AUTH_TYPE: string = "AuthType";
export const TOKEN_VALIDATION_URL: string = "TokenValidationURL";
export const READ_TIMEOUT_MS: string = "ReadTimeoutMS";
export const CONNECTION_TIMEOUT_MS: string = "ConnectionTimeoutMS";

/** External API Parameter Button Options */
const BODY_PARAMETER_OPTION: string = "AddExternalAPIParameterPrimaryBtn";
const HEADER_PARAMETER_OPTION: string = "AddExternalAPIHeaderParameterOption";
const QUERY_PARAMETER_OPTION: string = "AddExternalAPIQueryParameterOption";

export const OAuth2AuthType = OAUTH2_AUTH_TYPE;
export const APIKeyAuthType = API_KEY_AUTH_TYPE;

export const ExternalAPIParameterButtons: SplitButtonItem[] = [
	{
		res: BODY_PARAMETER_OPTION,
	},
	{
		res: HEADER_PARAMETER_OPTION,
	},
	{
		res: QUERY_PARAMETER_OPTION,
	},
];

export default class ExternalAPIWizardStore extends WizardStore {
	private readonly cache: ICache;
	private readonly externalAPIConnectorNode: C8Node;
	private readonly onClose: VoidFunction;
	private readonly onSave: ControllerSaveFunction;
	private readonly widget: ViewWidget;
	private readonly writer: Writer;
	private formFields: Record<string, APIField> = {};
	private _redrawData: boolean = false;
	private _responseJson: JsonResponse;
	private _minLoadingTime: number = 1500;
	additionalAuthParameters: AdditionalParameter[] = observable.array<AdditionalParameter>([], { deep: true });
	additionalConnectorParameters: AdditionalParameter[] = observable.array<AdditionalParameter>([], { deep: true });
	additionalGeneralProperties: AdditionalParameter[] = observable.array<AdditionalParameter>([], { deep: true });

	@observable private _cancelClicked: boolean = false;
	@observable private _isApiKeyInitialized: boolean = false;
	@observable private _isApiKeyHidden: boolean = true;
	@observable private _isClientSecretInitialized: boolean = false;
	@observable private _isClientSecretHidden: boolean = true;
	@observable private _isDelayedLoading: boolean = false;
	@observable private _isError: boolean = false;
	@observable private _isLoading: boolean = false;
	@observable private _isSuccess: boolean = false;
	@observable authType: string = OAuth2AuthType;
	@observable apiAddToError: boolean = false;
	@observable apiKey: string = "";
	@observable apiKeyError: boolean = false;
	@observable apiKeyName: string;
	@observable apiKeyNameError: boolean = false;
	@observable apiKeyAddTo: string;
	@observable clientID: string;
	@observable clientIDError: boolean = false;
	@observable clientSecret: string = "";
	@observable clientSecretError: boolean = false;
	@observable connectionTimeoutMS: number;
	@observable connectionTimeoutMSError: boolean = false;
	@observable errorMessage: string;
	@observable grantType: string;
	@observable tokenValidationURL: string;
	@observable tokenValidationURLError: boolean = false;
	@observable isAuthParametersVisible: boolean = true;
	@observable readTimeoutMS: number;
	@observable readTimeoutMSError: boolean = false;
	@observable scope: string;
	@observable tokenURL: string;
	@observable tokenURLError: boolean = false;

	get apiKeyAddToOptions(): ComponentItem[] {
		return [
			{
				id: "Header",
				res:"APIKeyAddTo:Header",
			},
			{
				id: "QueryParameter",
				res:"APIKeyAddTo:QueryParameter",
			},
		];
	}

	get authTypeOptions(): RadioOption<string>[] {
		const authTypeEnumStore = enums.getStore("APIAuthenticationType", null, null, true);
		const APIAuthCredentialTypeOptions = authTypeEnumStore.query().map((item) => {
			const authTypeVal = xml.enumGetText(item.id);
			return {
				name: this.locale.type(authTypeVal),
				value: authTypeVal,
				disabled: xml.enumGetText(this.externalAPIConnectorNode.AuthType) !== authTypeVal ? this.isReadonlyField(AUTH_TYPE): false,
			};
		});
		return APIAuthCredentialTypeOptions;
	}

	/** indicate whether a valid value was provided for ClientSecret. Empty values are valid if the secret is initialized with existing data in the server. */
	get isApiKeyValid(): boolean {
		return !!this.apiKey || this._isApiKeyInitialized;
	}

	/** indicate whether a valid ClientSecret exists in the database and should be displayed as dots */
	get isApiKeyHidden(): boolean {
		return this._isApiKeyInitialized && !this.apiKey && this._isApiKeyHidden;
	}

	/** Localization for APIKeyError */
	get apiKeyErrorHelperTextRes(): string {
		return this.apiKeyError ? "ValidateRequired" : "";
	}

	/** ComponentItem representing the currently selected apiKeyAddTo */
	get apiKeyAddToComponentItem(): ComponentItem<String> {
		const resultList = this.apiKeyAddToOptions.filter((option) => option.res === this.apiKeyAddTo);
		return resultList.length == 0 ? null : resultList.at(0);
	}

	/** Localization for APIKeyAddToError */
	get apiAddToErrorHelperTextRes(): string {
		return this.apiAddToError ? "ValidateRequired" : "";
	}

	/** Localization for APIKeyNameError */
	get apiKeyNameErrorHelperTextRes(): string {
		return this.apiKeyNameError ? "ValidateRequired" : "";
	}

	/** indicate whether a valid value was provided for ClientSecret. Empty values are valid if the secret is initialized with existing data in the server. */
	get isClientSecretValid(): boolean {
		return !!this.clientSecret || this._isClientSecretInitialized;
	}

	/** indicate whether a valid ClientSecret exists in the database and should be displayed as dots */
	get isClientSecretHidden(): boolean {
		return this._isClientSecretInitialized && !this.clientSecret && this._isClientSecretHidden;
	}

	/** Localization for ClientIDError */
	get clientIDErrorHelperTextRes(): string {
		return this.clientIDError ? "ValidateRequired" : "";
	}

	/** Localization for ClientSecretError */
	get clientSecretErrorHelperTextRes(): string {
		return this.clientSecretError ? "ValidateRequired" : "";
	}

	/** connection timeout time field's label value  */
	get connectionTimeoutMSLabel(): string {
		return this.locale.attributeResource("ExternalAPIConnector", "ConnectionTimeoutMS");
	}

	/** Localization for ConnectionTimeoutMSError */
	get connectionTimeoutMSErrorHelperTextRes(): string {
		if (!this.connectionTimeoutMSError) {
			return "";
		}

		if (this.connectionTimeoutMS < 0) {
			return "ValidateNaturalNumber";
		}

		return "ValidateRequired";
	}

	get externalAPIConnectorName(): string {
		return this.externalAPIConnectorNode.$Name;
	}

	/** health check url field's label value  */
	get tokenValidationURLLabel(): string {
		return this.locale.attributeResource("ExternalAPIConnector", "TokenValidationURL");
	}

	/** Localization for TokenValidationURLError */
	get tokenValidationURLErrorHelperTextRes(): string {
		return this.tokenValidationURLError ? "ValidateRequired" : "";
	}

	/** indicate whether or not to open the confirmation of selection dialog */
	get isCancelDialogOpen(): boolean {
		return !!this._cancelClicked;
	}

	/** indicate whether an error occurred and the error dialog should be shown */
	get isErrorDialogOpen(): boolean {
		return this._isError && !this.isLoadingDialogOpen;
	}

	/** indicate if the client is waiting for a response from the server */
	get isLoadingDialogOpen(): boolean {
		return this._isLoading || this._isDelayedLoading;
	}

	/** indicate that the authentication completed successfully */
	get isSuccessDialogOpen(): boolean {
		return this._isSuccess && !this.isLoadingDialogOpen;
	}

	/** indicate if the wizard dialog should be shown */
	get isWizardDialogOpen(): boolean {
		return !this._isLoading && !this._isError && !this._isSuccess;
	}

	/** read timeout time field's label value  */
	get readTimeoutMSLabel(): string {
		return this.locale.attributeResource("ExternalAPIConnector", "ReadTimeoutMS");
	}

	/** Localization for ReadTimeoutMSError */
	get readTimeoutMSErrorHelperTextRes(): string {
		if (!this.readTimeoutMSError) {
			return "";
		}

		if (this.readTimeoutMS < 0) {
			return "ValidateNaturalNumber";
		}

		return "ValidateRequired";
	}

	/** all the values of the required API Key Form fields */
	get requiredAPIKeyFormFields(): RequiredAPIKeyFields {
		return {
			apiKey: this.apiKey,
			apiKeyName: this.apiKeyName,
			apiKeyAddTo: this.apiKeyAddTo,
		};
	}

	/** all the values of the required Oauth Form fields */
	get requiredOauthFormFields(): RequiredOauthFields {
		return {
			clientID: this.clientID,
			clientSecret: this.clientSecret,
			tokenURL: this.tokenURL,
		};
	}

	/** all the values of the required Connector Form fields */
	get requiredConnectorFormFields(): RequiredConnectorFields {
		return {
			tokenValidationURL: this.tokenValidationURL,
			connectionTimeoutMS: this.connectionTimeoutMS,
			readTimeoutMS: this.readTimeoutMS,
		};
	}

	get requiredFirstPageFormFields(): RequiredAPIKeyFields | RequiredOauthFields {
		return this.authType === OAuth2AuthType ? this.requiredOauthFormFields : this.requiredAPIKeyFormFields;
	}

	/** Localization for TokenURLError */
	get tokenURLErrorHelperTextRes(): string {
		return this.tokenURLError ? "ValidateRequired" : "";
	}

	setAuthType = (value: string): void => {
		this.authType = value;
		this.isAuthParametersVisible = (value !== API_KEY_AUTH_TYPE);

		this.createAPIFields();
	};

	@action onAddParameter = (setupType: APISetupType, parameterType: string): void => {
		const additionalParameters = this.getAdditionalParameterList(setupType);
		additionalParameters.push(this.newExternalAPIParameter(parameterType));
	};

	@action onClickCloseError = () : void => {
		if (this._redrawData) {
			this.widget.redrawData();
		}

		this.onClose();
	};

	/** Call onSave with the operation results when the success dialog is closed */
	@action onClickSuccess = (): void => {
		this.onSave(this.getResponsePromise());
	};

	@action onRemoveParameter = (setupType: APISetupType, paramIdx: number): void => {
		const additionalParameters = this.getAdditionalParameterList(setupType);
		additionalParameters.splice(paramIdx, 1);
	};

	@action setAuthTypeChecked = (event: object, value: string): void => {
		this.setAuthType(value);
	};

	/** API Key Credentials Form fields */
	@action setAPIKeyName = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		this.apiKeyName = e.target.value.trim();
		this.formFields[API_KEY_NAME].value = this.apiKeyName;
		this.apiKeyNameError = false;
	};

	@action setAPIKey = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		this.apiKey = e.target.value.trim();
		this.formFields[API_KEY].value = this.apiKey;
		this.apiKeyError = false;
	};

	@action setAPIAddTo = (e: React.ChangeEvent<{}>, value: ComponentItem, reason: AutocompleteChangeReason): void => {
		if (value) {
			this.apiKeyAddTo = value.res;
			this.formFields[API_KEY_ADD_TO].value = this.apiKeyAddTo;
			this.apiAddToError = false;
		}
	};

	/** OAuth 2.0 Credentials Form fields */
	@action setClientID = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		this.clientID = e.target.value.trim();
		this.formFields[CLIENT_ID].value = this.clientID;
		this.clientIDError= false;
	};

	@action setClientSecret = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		this.clientSecret = e.target.value;
		this.formFields[CLIENT_SECRET].value = this.clientSecret;
		this.clientSecretError = false;
	};

	@action setTokenURL = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		this.tokenURL = e.target.value.trim();
		this.formFields[TOKEN_URL].value = this.tokenURL;
		this.tokenURLError= false;
	};

	@action setScope = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		this.scope = e.target.value.trim();
		this.formFields[SCOPE].value = this.scope;
	};

	@action setTokenValidationURLFilled = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		this.tokenValidationURL = e.target.value.trim();
		this.tokenValidationURLError = false;
	};

	@action setConnectionTimeoutMS = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		const newValue = Number(e.target.value.trim());
		this.connectionTimeoutMS = !Number.isNaN(newValue) ? newValue : 0;
		this.connectionTimeoutMSError = false;
	};

	@action setReadTimeoutMS = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		const newValue = Number(e.target.value.trim());
		this.readTimeoutMS = !Number.isNaN(newValue) ? newValue : 0;
		this.readTimeoutMSError = false;
	};

	@action setParameterKey = (parameters: Array<AdditionalParameter>, index: number, paramKey: string): void => {
		parameters[index].key = paramKey.trim();
	};

	@action setParameterValue = (parameters: Array<AdditionalParameter>, index: number, paramValue: string): void => {
		parameters[index].value = paramValue.trim();
	};

	@action showClientSecret = (): void => {
		this._isClientSecretHidden = false;
	};

	@action hideClientSecret = (): void => {
		this._isClientSecretHidden = true;
	};

	@action showApiKey = (): void => {
		this._isApiKeyHidden = false;
	};

	@action hideApiKey = (): void => {
		this._isApiKeyHidden = true;
	};

	@action cancelExternalAPIWizard = (): boolean => {
		return this.onCancel();
	};

	@action resetWizard = (): void => {
		this._isError = false;
		this.previousPage();
	};

	@action setFieldError = (fieldId: string): void => {
		switch (fieldId) {
			case "apiKey":
				this.apiKeyError = true;
				break;
			case "apiKeyName":
				this.apiKeyNameError = true;
				break;
			case "apiKeyAddTo":
				this.apiAddToError = true;
				break;
			case "clientID":
				this.clientIDError = true;
				break;
			case "clientSecret":
				this.clientSecretError = true;
				break;
			case "tokenURL":
				this.tokenURLError = true;
				break;
			case "tokenValidationURL":
				this.tokenValidationURLError = true;
				break;
			case "connectionTimeoutMS":
				this.connectionTimeoutMSError = true;
				break;
			case "readTimeoutMS":
				this.readTimeoutMSError = true;
				break;
		}
	};

	getAdditionalParameterTitle = (setupType: APISetupType): string => {
		switch (setupType) {
			case APISetupType.AUTHENTICATION:
				return this.locale.resource("ExternalAPIAdditionalAuthParametersTitle");

			case APISetupType.CONNECTOR:
				return this.locale.resource("ExternalAPIAdditionalConnectorParametersTitle");

			case APISetupType.GENERAL:
				return this.locale.resource("ExternalAPIAdditionalGeneralPropertiesTitle");
		}
	};

	/** determine the parameter type text of additional parameter */
	getAdditionalParameterKeyText = (parameterType: string): string => {
		switch (parameterType) {
			case BODY_PARAMETER_OPTION:
				return this.locale.resource("ExternalAPIAdditionalParameterBodyKeyText");
			case HEADER_PARAMETER_OPTION:
				return this.locale.resource("ExternalAPIAdditionalParameterHeaderKeyText");
			case QUERY_PARAMETER_OPTION:
				return this.locale.resource("ExternalAPIAdditionalParameterQueryKeyText");
			default:
				return this.locale.resource("ExternalAPIAdditionalParameterKeyText");
		}
	};

	getAdditionalParameterValueText = (): string => {
		return this.locale.resource("ExternalAPIAdditionalParameterValueText");
	};

	isReadonlyField = (fieldId: string): boolean => {
		return xml.valueHasFlag(this.formFields[fieldId].flags, xml.ATTR_FLAGS_READONLY);
	};

	/** 
	 *  @override  
	 *  setting _cancelClicked true means Close Dialog opens,
	 *  function returns the opposite so that dialog does not close immediately
	 */
	protected onCancel = (): boolean => {
		this._cancelClicked = !this._cancelClicked;
		return !this._cancelClicked;
	};

	/** extract information from form */
	protected onNextPage = async (index: number): Promise<boolean> => {
		if (index == 0) {
			// required field validation
			if (!this.validateFields(this.requiredFirstPageFormFields)) {
				return;
			}

			// side panel logic
			for (const id in this.formFields) {
				if (this.formFields[id].value === "") {
					// Adds name of field with <> to side panel if field value is blank
					// i.e. <Scope>
					this.stepInfoList[0].values.push("<" + this.formFields[id].label + ">");
				} else if (id === GRANT_TYPE) {
					// grant type displays as "Client Credentials"
					// but is stored as "client_credentials"
					this.grantType = CLIENT_CREDENTIALS;
					this.stepInfoList[0].values.push("Client Credentials");
				} else if (id === API_KEY_ADD_TO) {
					this.stepInfoList[0].values.push(this.locale.resource(this.formFields[id].value));
				} else {
					this.stepInfoList[0].values.push(this.formFields[id].value);
				}
			}
		}
		return true;
	};

	/** 
	 * @override 
	 * Logic that occurs after the "Finish" button is pressed
	 */
	protected onSavePromise = async (): Promise<JsonResponse> => {
		// required field validation
		if (!this.validateFields(this.requiredFirstPageFormFields) || !this.validateFields(this.requiredConnectorFormFields)) {
			return;
		}

		this._isDelayedLoading = true;
		this._isLoading = true;

		try {
			this.callSetupOperation();
			setTimeout(() => this._isDelayedLoading = false, this._minLoadingTime);
			this._responseJson = await submitWriter(this.writer);
			this._isSuccess = true;

			return this._responseJson;
		}
		catch (e) {
			this.errorMessage = e.message;
			this._isError = true;

			// Hanlde setup state
			if (this.externalAPIConnectorNode.APISetup === "ExternalAPIConnectorSetup:NotStarted") {
				this.externalAPIConnectorNode.APISetup = "ExternalAPIConnectorSetup:Failed";
				this.cache.cacheNode(this.externalAPIConnectorNode);
				this._redrawData = true;
			}
		}
		finally {
			this._isLoading = false;
		}

		return;
	};

	/**
	 * Validates each field in the given list and sets the corresponding error if there are any
	 * Returns true if all fields are valid or false if any fields are invalid
	 */
	private validateFields = (requiredFields: RequiredFields): boolean => {
		let isValid = true;

		for (const fieldId in requiredFields) {
			// Field value cannot be undefined or empty string
			const fieldValue = requiredFields[fieldId];
			// API Key and Client ID have separate validation logic
			const validApiKey = fieldId === "apiKey" && this.isApiKeyValid;
			const validClientSecret = fieldId === "clientSecret" && this.isClientSecretValid;
			const populatedField = !!fieldValue || validApiKey || validClientSecret;
			// Timeout values are populated but not positive integers
			const invalidConnectionTimeout = fieldId === "connectionTimeoutMS" && this.connectionTimeoutMS <= 0;
			const invalidReadTimeout = fieldId === "readTimeoutMS" && this.readTimeoutMS <= 0;

			// Check that all rquired fields are populated
			if (!populatedField || invalidConnectionTimeout || invalidReadTimeout) {
				this.setFieldError(fieldId);
				isValid = false;
			}
		}

		return isValid;
	};

	private getResponsePromise = async (): Promise<JsonResponse> => {
		return this._responseJson;
	};

	/** 
	 * Create a new ExternalAPI Parameter object
	 */
	private newExternalAPIParameter(parameterType: string): AdditionalParameter {
		return ({
			key: "",
			type: parameterType,
			value: "",
		});
	};

	/* 
	 * Create API fields to store information for fields from first page of wizard 
	 */
	private createAPIFields = (): void => {
		this.formFields = {};
		const connectorNode = this.externalAPIConnectorNode;
		const authCredentialsNode = this.cache.get(connectorNode.Credentials);

		// First page fields
		this.formFields[AUTH_TYPE] = { label: this.locale.attributeResource("ExternalAPIConnector", AUTH_TYPE), value: this.authType, flags: xml.nodeAttributeGetFlags(connectorNode, AUTH_TYPE) };
		if (this.authType === OAuth2AuthType){
			this.formFields[CLIENT_ID] = { label: this.locale.attributeResource("OAuth2Credentials", CLIENT_ID), value: this.clientID, flags: xml.nodeAttributeGetFlags(authCredentialsNode, CLIENT_ID) };
			this.formFields[CLIENT_SECRET] = { label: this.locale.attributeResource("OAuth2Credentials", CLIENT_SECRET), value: this._isClientSecretInitialized ? "\u{2022}".repeat(20) : this.clientID, flags: 0 };
			this.formFields[GRANT_TYPE] = { label: "Grant Type", value: CLIENT_CREDENTIALS, flags: xml.ATTR_FLAGS_READONLY };
			this.formFields[SCOPE] = { label: this.locale.attributeResource("OAuth2Credentials", SCOPE), value: this.scope, flags: xml.nodeAttributeGetFlags(authCredentialsNode, SCOPE) };
			this.formFields[TOKEN_URL] = { label: this.locale.resource("TokenURL"), value: this.tokenURL, flags: xml.nodeAttributeGetFlags(authCredentialsNode, TOKEN_URL) };
		} else if (this.authType === APIKeyAuthType) {
			this.formFields[API_KEY_NAME] = { label: this.locale.attributeResource("APIKeyCredentials", API_KEY_NAME), value: this.apiKeyName, flags: xml.nodeAttributeGetFlags(authCredentialsNode, API_KEY_NAME) };
			this.formFields[API_KEY] = { label: this.locale.attributeResource("APIKeyCredentials", API_KEY), value: this._isApiKeyInitialized ? "\u{2022}".repeat(20) : this.apiKey, flags: xml.nodeAttributeGetFlags(authCredentialsNode, API_KEY) };
			this.formFields[API_KEY_ADD_TO] = { label: this.locale.attributeResource("APIKeyCredentials", API_KEY_ADD_TO), value: this.apiKeyAddTo, flags: xml.nodeAttributeGetFlags(authCredentialsNode, API_KEY_ADD_TO) };
		}

		// Second page fields
		this.formFields[TOKEN_VALIDATION_URL] = { label: this.tokenValidationURLLabel, value: this.tokenValidationURL, flags: xml.nodeAttributeGetFlags(connectorNode, TOKEN_VALIDATION_URL) };
		this.formFields[CONNECTION_TIMEOUT_MS] = { label: this.connectionTimeoutMSLabel, value: this.connectionTimeoutMS, flags: xml.nodeAttributeGetFlags(connectorNode, CONNECTION_TIMEOUT_MS) };
		this.formFields[READ_TIMEOUT_MS] = { label: this.readTimeoutMSLabel, value: this.readTimeoutMS, flags: xml.nodeAttributeGetFlags(connectorNode, READ_TIMEOUT_MS) };
	};

	/* 
	 * Return all the additional parameters of passed in type (BODY, HEADER, QUERY) from list of Additional Parameters
	 */
	private getAdditionalParameters = (setupType: APISetupType, parameterType: string): Record<string, string> => {
		const allParameters = {};
		const additionalParameters = this.getAdditionalParameterList(setupType);

		for (const parameter of additionalParameters) {
			if (parameter.type === parameterType) {
				allParameters[parameter.key] = parameter.value;
			}
		}

		return allParameters;
	};

	/*
	 * Return the corresponding AdditionalParameter list given an APISetupType
	 */
	private getAdditionalParameterList = (setupType: APISetupType): AdditionalParameter[] => {
		switch (setupType) {
			case APISetupType.AUTHENTICATION:
				return this.additionalAuthParameters;

			case APISetupType.CONNECTOR:
				return this.additionalConnectorParameters;

			case APISetupType.GENERAL:
				return this.additionalGeneralProperties;
		}
	};

	private callSetupOperation = (): void => {
		const externalAPIConnectorNode = this.externalAPIConnectorNode;
		const externalAPIConnectorURL = externalAPIConnectorNode.$URL;

		/** Get field values */
		const credentialBodyParameters = this.getAdditionalParameters(APISetupType.AUTHENTICATION, BODY_PARAMETER_OPTION);
		const credentialHeaderParameters = this.getAdditionalParameters(APISetupType.AUTHENTICATION, HEADER_PARAMETER_OPTION);
		const credentialQueryParameters = this.getAdditionalParameters(APISetupType.AUTHENTICATION, QUERY_PARAMETER_OPTION);
		const connectorBodyParameters = this.getAdditionalParameters(APISetupType.CONNECTOR, BODY_PARAMETER_OPTION);
		const connectorHeaderParameters = this.getAdditionalParameters(APISetupType.CONNECTOR, HEADER_PARAMETER_OPTION);
		const connectorQueryParameters = this.getAdditionalParameters(APISetupType.CONNECTOR, QUERY_PARAMETER_OPTION);
		const connectorGeneralProperties = this.getAdditionalParameters(APISetupType.GENERAL, null);

		/**  Add ExternalAPIConnector parameters */
		const callMethod = this.writer.callMethod(MODULE_SITEADMIN, AUTH_TYPE_OPERATION_MAP.get(this.authType))
			.addParameter(PARENT_URL, xml.ATTR_CNL, externalAPIConnectorURL)
			.addParameter(TOKEN_VALIDATION_URL, xml.ATTR_STRING, this.tokenValidationURL)
			.addParameter(CONNECTOR_BODY_PARAMETERS, xml.ATTR_STRINGMAP, connectorBodyParameters)
			.addParameter(CONNECTOR_HEADER_PARAMETERS, xml.ATTR_STRINGMAP, connectorHeaderParameters)
			.addParameter(CONNECTOR_QUERY_PARAMETERS, xml.ATTR_STRINGMAP, connectorQueryParameters)
			.addParameter(CONNECTOR_GENERAL_PROPERTIES, xml.ATTR_STRINGMAP, connectorGeneralProperties)
			.addParameter(CONNECTION_TIMEOUT_MS, xml.ATTR_INTEGER, this.connectionTimeoutMS)
			.addParameter(READ_TIMEOUT_MS, xml.ATTR_INTEGER, this.readTimeoutMS);

		/** Add APIAuthCredentials parameters */
		if (this.authType === API_KEY_AUTH_TYPE) {
			callMethod
				.addParameter(API_KEY_NAME, xml.ATTR_STRING, this.apiKeyName)
				.addParameter(API_KEY, xml.ATTR_STRING, this.apiKey)
				.addParameter(API_KEY_ADD_TO, xml.ATTR_ENUM, this.apiKeyAddTo);
		}
		/** Add OAuth parameters */
		else if (this.authType === OAUTH2_AUTH_TYPE) {
			callMethod
				.addParameter(AUTH_BODY_PARAMETERS, xml.ATTR_STRINGMAP, credentialBodyParameters)
				.addParameter(AUTH_HEADER_PARAMETERS, xml.ATTR_STRINGMAP, credentialHeaderParameters)
				.addParameter(AUTH_QUERY_PARAMETERS, xml.ATTR_STRINGMAP, credentialQueryParameters)
				.addParameter(CLIENT_ID, xml.ATTR_STRING, this.clientID)
				.addParameter(CLIENT_SECRET, xml.ATTR_STRING, this.clientSecret)
				.addParameter(TOKEN_URL, xml.ATTR_STRING, this.tokenURL)
				.addParameter(SCOPE, xml.ATTR_STRING, this.scope);
		}
	};

	private initializeFields = (): void => {
		// ExternalAPIConnector attributes
		this.tokenValidationURL = this.externalAPIConnectorNode.TokenValidationURL;
		this.connectionTimeoutMS = this.externalAPIConnectorNode.ConnectionTimeoutMS;
		this.readTimeoutMS =  this.externalAPIConnectorNode.ReadTimeoutMS;

		// Add connector parameters
		this.addConnectorParameters(this.externalAPIConnectorNode.ConnectorBodyParameters, BODY_PARAMETER_OPTION);
		this.addConnectorParameters(this.externalAPIConnectorNode.ConnectorHeaderParameters, HEADER_PARAMETER_OPTION);
		this.addConnectorParameters(this.externalAPIConnectorNode.ConnectorQueryParameters, QUERY_PARAMETER_OPTION);
		this.addGeneralProperties(this.externalAPIConnectorNode.ConnectorGeneralProperties);

		// If the ExternalAPIConnector has a credentials node, populate the corresponding fields
		const credentialsNode = this.cache.get(this.externalAPIConnectorNode.Credentials);

		if (!credentialsNode) {
			return;
		}

		// Add authentication parameters
		this.setAuthType(credentialsNode.$Type);
		this.addAuthParameters(credentialsNode.AuthBodyParameters, BODY_PARAMETER_OPTION);
		this.addAuthParameters(credentialsNode.AuthHeaderParameters, HEADER_PARAMETER_OPTION);
		this.addAuthParameters(credentialsNode.AuthQueryParameters, QUERY_PARAMETER_OPTION);

		if (this.authType === OAUTH2_AUTH_TYPE) {
			this.initializeOAuth2Fields(credentialsNode);
		}
		else if (this.authType == API_KEY_AUTH_TYPE) {
			this.initalizeAPIKeyFields(credentialsNode);
		}
	};

	private initalizeAPIKeyFields = (apiAuthCredentials: C8Node): void => {
		this.apiKeyName = apiAuthCredentials.KeyName;
		this.apiKeyAddTo = apiAuthCredentials.APIKeyAddTo;

		this._isApiKeyInitialized = true;
	};

	private initializeOAuth2Fields = (apiAuthCredentials: C8Node): void => {
		this.clientID = apiAuthCredentials.ClientId;
		this.tokenURL = apiAuthCredentials.AuthenticationServerURL;
		this.scope = apiAuthCredentials.Scope;

		// If ClientID and TokenURL have pre-existing value, then the authentication has already once been set up
		if (this.clientID && this.tokenURL) {
			this._isClientSecretInitialized = true;
		}
	};

	private addAuthParameters = (additionalParams: Map<string, string>, type: string): void => {
		_.forEach(additionalParams, (value: string, key: string) => {
			this.additionalAuthParameters.push({ key: key, value: value, type: type });
		});
	};

	private addConnectorParameters = (additionalParams: Map<string, string>, type: string): void => {
		_.forEach(additionalParams, (value: string, key: string) => {
			this.additionalConnectorParameters.push({ key: key, value: value, type: type });
		});
	};

	private addGeneralProperties = (generalProperties: Map<string, string>): void => {
		_.forEach(generalProperties, (value: string, key: string) => {
			this.additionalGeneralProperties.push({ key: key, value: value, type: null });
		});
	};

	constructor(
		locale: ILocaleProvider,
		writer: Writer,
		cache: ICache,
		widget: ViewWidget,
		externalAPIConnectorNode: C8Node,
		onClose?: VoidFunction,
		onSave?: ControllerSaveFunction,
	) {
		// Pass an empty onSave function to parent constructor. onSave will be stored and called when the SuccessDialog is closed.
		super(2, "ExternalAPISetup", onClose, () => {}, locale);
		makeObservable(this);

		this.writer = writer;
		this.cache = cache;
		this.widget = widget;
		this.externalAPIConnectorNode = externalAPIConnectorNode;
		// Store the onSave method to call when Success dialog is closed
		this.onSave = onSave;
		this.onClose = onClose;
		
		// Create fields and initialize using ExternalAPIConnector and APIAuthCredentials nodes
		this.initializeFields();
		this.createAPIFields();
	}
}

export const ExternalAPIWizardStoreContext = createContext<ExternalAPIWizardStore>(undefined);
export const useExternalAPIWizardStore = (): ExternalAPIWizardStore => useContext(ExternalAPIWizardStoreContext);
